import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getUser } from "../utils/auth";
import {
  IconPlus,
  IconFolders,
  IconTimelineEvent,
  IconSettings,
  IconUsers,
  IconListCheck,
  IconUserEdit,
  IconCoins,
  IconReceipt2,
  IconApi,
  IconDatabaseSearch,
  IconChecklist,
  IconPremiumRights,
  IconCreditCard,
  IconLogout,
  IconEyeDollar,
  IconSquareRoundedChevronsLeft,
  IconMail,
  IconSquareRoundedChevronsRight
} from "@tabler/icons-react";

const Sidebar = ({ logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [user, setUser] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1200);

  // 🔐 Persist collapsed state on every change
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const fetchUser = async () => {
      const userData = await getUser();
      setUser(userData);
    };

    const handleResize = () => {
      const desktop = window.innerWidth > 1200;
      setIsDesktop(desktop);
      // Auto-expand on small screens only
      if (!desktop) setIsCollapsed(false);
    };

    window.addEventListener("resize", handleResize);
    fetchUser();
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const iconSize = 24;
  const displayName = user?.firstname || "User";

  return (
    <aside className={`navbar navbar-vertical navbar-expand-lg${isCollapsed ? " sidebar-collapsed" : ""}`} data-bs-theme="dark">
      <div className="container-fluid" style={{ fontSize: "16px" }}>
        <button
          className={`navbar-toggler ${isOpen ? "" : "collapsed"}`}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {!isCollapsed && (
          <div className="navbar-brand navbar-brand-autodark text-center d-block">
            <a href="/">
              <img src="/images/logo.webp" alt="IndexBooster" className="navbar-brand-image" width="110" height="32" />
            </a>
          </div>
        )}

        <div className={`navbar-collapse collapse ${isOpen ? "show" : ""}`} id="sidebar-menu">
          <ul className="navbar-nav pt-lg-3">
            {!isCollapsed && (
              <>
                <li className="nav-item">
                  <div className="nav-link text-muted">Hi, {displayName}!</div>
                </li>
                <li className="nav-item">
                  <div className="nav-link text-white fw-bold">Credits: {user?.credits ?? 0}</div>
                </li>
              </>
            )}
            <li className="nav-item">
              <Link className="nav-link" to="/indexing/add" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconPlus size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Add Project</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconFolders size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">My Projects</span>}
              </Link>
            </li>

            <li className="nav-item">
              <Link className="nav-link" to="/buy-credits" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconCreditCard size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Buy Credits</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/my-orders" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconPremiumRights size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">My Orders</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/credit-history" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconCoins size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Credit History</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/api-indexbooster" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconApi size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">API IndexBooster</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/profile" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconUserEdit size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Edit Profile</span>}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/contact-us" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
                <span className="nav-link-icon"><IconMail size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Contact us</span>}
              </Link>
            </li>
            <li className="nav-item">
              <button className="nav-link text-danger w-100 text-start" onClick={logout}>
                <span className="nav-link-icon text-danger"><IconLogout size={iconSize} /></span>
                {!isCollapsed && <span className="nav-link-title ms-2">Logout</span>}
              </button>
            </li>

{user?.role === "admin" && (
  <>
    {!isCollapsed && (
      <>
        <hr className="my-4" />
        <li className="nav-item">
          <div className="nav-link text-green fw-bold">Admin Tools</div>
        </li>
      </>
    )}

    <li className="nav-item">
      <Link className="nav-link" to="/credit-history-admin" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconReceipt2 size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">All Credit History</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/all-orders" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconEyeDollar size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">All Orders</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/all-project" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconChecklist size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">All Project</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/all-tasks" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconChecklist size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">All Tasks</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/users" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconUsers size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">All Users</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/api-logs" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconDatabaseSearch size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">API Logs</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/logs" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconTimelineEvent size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">Googlebot Logs</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/settings" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconSettings size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">Settings</span>}
      </Link>
    </li>

    <li className="nav-item">
      <Link className="nav-link" to="/task-manager" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconListCheck size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">Task Manager</span>}
      </Link>
    </li>
        <li className="nav-item">
      <Link className="nav-link" to="/manual-index-tasks" onClick={() => window.innerWidth < 1200 && setIsOpen(false)}>
        <span className="nav-link-icon"><IconListCheck size={iconSize} /></span>
        {!isCollapsed && <span className="nav-link-title ms-2">Manual Indexing</span>}
      </Link>
    </li>
    
  </>
)}

            {isDesktop && (
              <li className="nav-item mt-3">
                <button
                  className="nav-link w-100 text-start text-secondary"
                  onClick={() => { setIsCollapsed(!isCollapsed); setIsOpen(false); }}
                >
                  <span className="nav-link-icon">
                    {isCollapsed ? <IconSquareRoundedChevronsRight size={iconSize} /> : <IconSquareRoundedChevronsLeft size={iconSize} />}
                  </span>
                  {!isCollapsed && <span className="nav-link-title ms-2">Collapse Menu</span>}
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
